import React, { useCallback, useEffect, useRef } from 'react';
import { get, flowRight } from 'lodash';

import { type WithTranslation } from '@wix/yoshi-flow-editor';
import {
  SECTION_CATEGORY,
  isLayoutProGallery,
  createPageUrl,
} from '@wix/communities-blog-client-common';
import stylesParams from '../../../../components/Blog/stylesParams';
import AnimatedLoader from '../../../common/components/animated-loader';
import withComponents, {
  type WithComponents,
} from '../../../common/components/components-provider/with-components';
import { EmptyLayout } from '../../../common/components/layout';
import Page from '../../../common/components/page';
import { connect } from '../../../common/components/runtime-context';
import Pagination from '../../../common/containers/pagination';
import { PostTitleTagProvider } from '../../../common/context/post-title-tag-context';
import withAuth, { type WithAuth } from '../../../common/hoc/with-auth';
import withLayoutProps, { type WithLayoutProps } from '../../../common/hoc/with-layout-props';
import withPermissions, { type WithPermissions } from '../../../common/hoc/with-permissions';
import withTranslate from '../../../common/hoc/with-translate';
import {
  isExcludePostContentSupported,
  isPostContentRequired,
} from '../../../common/selectors/layout-selectors';
import { getSortedCategoryPosts, getPostsByPage } from '../../../common/selectors/post-selectors';
import { getHashtagUrl } from '../../../common/services/getHashtagUrl';
import { handleNavigation } from '../../../common/services/navigation';
import { isSite } from '../../../common/store/basic-params/basic-params-selectors';
import { getIsHashtagPostsLoaded } from '../../../common/store/is-loaded/is-loaded-selectors';
import { getIsHashtagPostsLoading } from '../../../common/store/is-loading/is-loading-selectors';
import { getSectionUrl } from '../../../common/store/topology/topology-selectors';
import PostList from '../../components/post-list';

import withPaginationSettings, {
  type WithPaginationSettings,
} from '../../hoc/with-pagination-settings';

const Layout = EmptyLayout;

type Props = OwnProps &
  WithComponents &
  WithPermissions &
  WithPaginationSettings &
  WithLayoutProps &
  WithAuth &
  Pick<WithTranslation, 't'> &
  ReturnType<typeof mapRuntimeToProps>;

const HashtagPage: React.FC<Props> = (props) => {
  const {
    hashtag,
    currentPagePosts,
    allPosts,
    entityCount,
    page,
    pageSize,
    layoutType,
    showPagination,
    isHashtagLoading,
    isHashtagLoaded,
    hashtagUrl,
    isSite,
    PostListProGallery,
  } = props;
  // Read by the stable loadMore and the unmount cleanup, so must be the latest render's
  const latest = useRef(props);
  latest.current = props;
  const prev = useRef<Props>();

  const fetchPosts = (hashtag: string, page = latest.current.page) => {
    if (latest.current.isBlocked) {
      return;
    }

    return latest.current.fetchHashtagPosts(hashtag, page);
  };

  useEffect(() => {
    const prevProps = prev.current;
    prev.current = props;
    if (!prevProps) {
      return;
    }

    if (
      props.isAuthenticated !== prevProps.isAuthenticated ||
      props.isBlocked !== prevProps.isBlocked ||
      props.pageSize !== prevProps.pageSize ||
      (prevProps.excludeContent && !props.excludeContent)
    ) {
      fetchPosts(hashtag);
    }
  });

  useEffect(() => () => latest.current.setIsLoaded('hashtag-posts', null, true), []);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const loadMore = useCallback((page: number) => fetchPosts(latest.current.hashtag, page), []);

  const renderPosts = () => {
    if (layoutType && isLayoutProGallery(layoutType)) {
      return (
        <PostListProGallery
          pageStart={page + 1}
          entityCount={entityCount}
          loadMore={showPagination ? undefined : loadMore}
          allPosts={showPagination ? currentPagePosts : allPosts}
          currentPagePosts={currentPagePosts}
          isLoading={isHashtagLoading}
          section={SECTION_CATEGORY}
        />
      );
    }

    return (
      <PostList
        pageStart={page + 1}
        layoutType={layoutType}
        entityCount={entityCount}
        loadMore={showPagination ? undefined : loadMore}
        allPosts={showPagination ? currentPagePosts : allPosts}
        currentPagePosts={currentPagePosts}
        isLoading={isHashtagLoading}
      />
    );
  };

  const isLoading = isHashtagLoading && !isHashtagLoaded;

  return (
    <PostTitleTagProvider titleFontParam={stylesParams.cssTitleFontCategory} fallbackTagName="h2">
      <Page noSpacing>
        <Layout>
          <AnimatedLoader isLoading={isLoading && !allPosts.length}>{renderPosts()}</AnimatedLoader>
          {!isLoading && showPagination ? (
            <Pagination
              dataHook="hashtag-pagination-container"
              page={page}
              pageSize={pageSize}
              entityCount={entityCount}
              createPageUrl={(page) => createPageUrl(page, hashtagUrl)}
              handleNavigation={handleNavigation('hashtag', isSite)}
            />
          ) : null}
        </Layout>
      </Page>
    </PostTitleTagProvider>
  );
};

type OwnProps = {
  params: { hashtag: string };
  page: number;
  canSee: (action: string, entity: string) => boolean;
};

import { type Actions, type AppState } from '../../../common/types';

const mapRuntimeToProps = (state: AppState, ownProps: OwnProps, actions: Actions) => {
  const hashtag = get(ownProps, ['params', 'hashtag']);

  const sectionUrl = getSectionUrl(state);

  return {
    hashtag,
    hashtagUrl: getHashtagUrl(sectionUrl, hashtag),
    allPosts: hashtag ? getSortedCategoryPosts(state) : [],
    currentPagePosts: hashtag ? getPostsByPage(state, ownProps.page) : [],
    isHashtagLoading: getIsHashtagPostsLoading(state),
    isHashtagLoaded: getIsHashtagPostsLoaded(state),
    isSite: isSite(state),
    excludeContent: isExcludePostContentSupported(state),
    setIsLoaded: actions.setIsLoaded,
    fetchHashtagPosts: (hashtag: string, page: number) =>
      actions.fetchHashtagPostsWithAdapterPromisified({
        page,
        includeContent: isPostContentRequired(state),
        hashtagOptions: { hashtag },
      }),
  };
};

export default flowRight(
  withPermissions,
  withComponents,
  withPaginationSettings(SECTION_CATEGORY),
  withLayoutProps(),
  withAuth,
  withTranslate,
  connect(mapRuntimeToProps),
)(HashtagPage);
